import type { VisibilityState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { PROJECT } from "@/app/capabilities/permissions";
import type { Secret } from "@/app/secrets/types";
import { DataTable } from "@/components/custom/table/data-table";
import { DataTableContent } from "@/components/custom/table/data-table-content";
import { DataTablePagination } from "@/components/custom/table/data-table-pagination";
import { createSecretColumns } from "./columns";

type SecretsTableProps = {
  items: Secret[];
  stickyColumnBg?: "card" | "background";
  overriddenSecrets?: Set<string>;
  overridingSecrets?: Set<string>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (
    updater: VisibilityState | ((old: VisibilityState) => VisibilityState)
  ) => void;
};

export const SecretsTable = ({
  items,
  stickyColumnBg = "background",
  overriddenSecrets,
  overridingSecrets,
  columnVisibility,
  onColumnVisibilityChange,
}: SecretsTableProps) => {
  const { canProject } = useProjectPermissions();
  const showActions =
    canProject(PROJECT.secrets.update) || canProject(PROJECT.secrets.delete);
  const columns = useMemo(
    () =>
      createSecretColumns({
        overriddenSecrets,
        overridingSecrets,
        showActions,
      }),
    [overriddenSecrets, overridingSecrets, showActions]
  );

  return (
    <DataTable
      className="space-y-4"
      columns={columns}
      columnVisibility={columnVisibility}
      data={items}
      defaultPageSize={5}
      onColumnVisibilityChange={onColumnVisibilityChange}
    >
      <DataTableContent stickyColumnBg={stickyColumnBg} />
      <DataTablePagination />
    </DataTable>
  );
};
